import { CircleExclamationFill } from '@gravity-ui/icons';
import { ErrorMessage as HeroErrorMessage } from '@heroui/react';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type RemovePrefix<T extends string, Prefix extends T> = T extends `${Prefix}${string}` ? never : T;

/**
 * All error codes that can be passed to ErrorMessage.
 * Nested keys are removed since they will result in a non-string return value. They
 * can be processed manually.
 */
type ErrorCode = RemovePrefix<TFuncKey<'translation', 'error'>, 'password_rejected'>;

export type ErrorType = ErrorCode | { code: ErrorCode; data?: Record<string, unknown> };

export type Props = {
  readonly error?: ErrorType;
  readonly className?: string;
  readonly children?: ReactNode;
  /** Stable id so a field can reference this message via aria-errormessage/aria-describedby. */
  readonly id?: string;
};

const ErrorMessage = ({ error, className, children, id }: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'error' });

  const translate = (error: ErrorType) =>
    typeof error === 'string' ? t(error) : t(error.code, { ...error.data });

  const message = error ? translate(error) : children;

  // Mount only with content: a live region announces reliably on insertion, not on a text
  // change inside an always-present empty node.
  if (!message) {
    return null;
  }

  return (
    <HeroErrorMessage
      id={id}
      role="alert"
      elementType="div"
      className={classNames(
        'flex items-start gap-1.5 text-sm animate-in fade-in slide-in-from-top-1 duration-(--duration-quick) ease-(--ease-out-strong) motion-reduce:slide-in-from-top-0',
        className
      )}
    >
      {/* The danger hue can sit close to a red tenant accent, so the icon carries the signal. */}
      <CircleExclamationFill aria-hidden="true" className="h-lh w-4 shrink-0" />
      {/* Upstream templates interpolate a lowercase field name first ("email is required").
          ::first-letter needs a block container, which the flex parent is not. */}
      <span className="block min-w-0 first-letter:uppercase [&_li]:first-letter:uppercase [&_ul]:list-disc [&_ul]:ps-4">
        {message}
      </span>
    </HeroErrorMessage>
  );
};

export default ErrorMessage;
